import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/react-hooks";

import { httpClient } from "../../settings/apolloClient";
import { InstrumentsPanelWrapper, InfoParagraph } from "../../components/StyledComponents";
import { API } from "../../constants/shipmentType";
import AlertModal from "../../components/AlertModal/AlertModal";

import InstrumentsPanelContent from "./InstrumentsPanelContent";
import LOADING_CONCRETE_SHIPMENT from "./graphql/queries/loadingConcreteShipment.js";
import PLANT_LOCATIONS_QUERY from "./graphql/queries/plantLocations";
import ControlMenu from "./ControlMenu/ControlMenu";
import { ManualOperation } from "./ManualOperation/ManualOperation";

import { CURRENT_AUTOMATION_STATUS_QUERY } from "../AutomationStatus/automationStatusQueries";

import { Sensors } from "./Sensors/Sensors";
import SENSORS_QUERY from "./graphql/queries/sensorsQuery";

import CURRENT_USER_QUERY from "./graphql/queries/currentUserQuery";
import INVENTORY_ITEMS_LOCATIONS_QUERY from "./graphql/queries/inventoryItemsLocationsQuery";
import INVENTORY_LOCATIONS_QUERY from "./graphql/queries/inventoryLocationsQuery";

import WAITING_SHIPMENT_REQUEST_QUERY from "./graphql/queries/waitingShipmentRequest";

import { CopyButton } from "../../components/CopyButton/CopyButton";
import ShipmentRequestModal from "./ShipmentModal/ShipmentRequestModal.jsx";
import {
  useGetCameraList,
} from "api/controller-configs";

import { CameraWrapper } from "./Camera/CameraWrapper.jsx";
import Notifications from "./Notifications/Notifications.jsx"

import "./InstrumentsPanel.scss";

const PAGE_CLASSES = [".main-panel", ".navbar", ".map-content"];

/**
 * Defines the poll interval for loadingShipmentQuery when
 * there isn't any loading shipment yet.
 */
const POLL_INTERVAL = 2000;
const CAMERA_HEIGHT = 300;
// const SCREEN_MIN_WIDTH = 0;
const MOBILE_WIDTH = 1000;

const InstrumentsPanel = ({
  sidebarCollapsed,
}) => {
  const [
    shown,
    setShown,
  ] = useState(false);

  const [isSRMOpen, setIsSRMOpen] = useState(false);

  const [expanded, setExpanded] = useState(false);
  const [expansionHeight, setExpansionHeight] = useState(`${CAMERA_HEIGHT}px`);
  const restrictedUsers = new Set(["API_USER", "RESTRICTED_USER"]);
  const isMobile = (window.innerWidth <= MOBILE_WIDTH);
  const { cameraList } = useGetCameraList();

  useEffect(() => {
    document.body.classList.toggle("sidebar-mini");
    document.body.classList.add("instruments-panel");

    PAGE_CLASSES.forEach(el => document.querySelector(el).classList.add("instruments-panel"));

    return () => {
      document.body.classList.toggle("sidebar-mini");
      document.body.classList.add("instruments-panel");

      PAGE_CLASSES.forEach(el => document.querySelector(el).classList.remove("instruments-panel"));
    };
  }, []);

  const [isControlMenuOpen, setIsControlMenuOpen] = useState(false);
  const handleControlMenuOpen = (open) => {
    setIsControlMenuOpen(open);
  };

  const {
    data: loadingConcreteShipmentData,
    loading,
    refetch,
    stopPolling,
    startPolling,
  } = useQuery(LOADING_CONCRETE_SHIPMENT, {
    fetchPolicy: "network-only",
    pollInterval: POLL_INTERVAL,
    client: httpClient,
  });

  const {
    data: waitingShipmentRequestData,
    loading: wsrLoading,
    startPolling: wsrStartPolling,
    stopPolling: wsrStopPolling,
  } = useQuery(WAITING_SHIPMENT_REQUEST_QUERY, {
    fetchPolicy: "network-only",
    pollInterval: POLL_INTERVAL,
    client: httpClient,
  });

  const {
    waitingShipmentRequest,
  } = waitingShipmentRequestData || {};

  const { data: automationStatus } = useQuery(CURRENT_AUTOMATION_STATUS_QUERY, {
    fetchPolicy: "network-only",
    pollInterval: POLL_INTERVAL,
    client: httpClient,
  });

  const { data: sensorsData } = useQuery(SENSORS_QUERY, {
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const { sensors } = sensorsData || [];

  const { data: getCurrentUserData } = useQuery(CURRENT_USER_QUERY, {
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const { getCurrentUser } = getCurrentUserData || {};

  const {
    loadingConcreteShipment,
    manualOperations,
  } = loadingConcreteShipmentData || {};

  const {
    data: devicesData,
  } = useQuery(PLANT_LOCATIONS_QUERY, {
    client: httpClient,
    variables: {
      concreteShipmentId: loadingConcreteShipment ? loadingConcreteShipment.id : 0,
    },
    fetchPolicy: "network-only",
    pollInterval: POLL_INTERVAL * 2,
  });

  const { data: inventoryItemsData } = useQuery(INVENTORY_ITEMS_LOCATIONS_QUERY, {
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const {
    inventoryItems,
  } = inventoryItemsData || {};

  const { data: inventoryLocationsData } = useQuery(INVENTORY_LOCATIONS_QUERY, {
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const {
    inventoryLocations,
  } = inventoryLocationsData || {};

  useEffect(() => {
    if (loadingConcreteShipment) {
      stopPolling();
      wsrStopPolling();

      if (loadingConcreteShipment.shipmentType === API && !shown) {
        setShown(true);

        AlertModal.fire({
          title: "Carregamento iniciado remotamente",
          type: "info",
          html: (
            <InfoParagraph>
              Não se esqueça de marcar quando o caminhão estiver pronto
            </InfoParagraph>
          ),
          showCancelButton: false,
          confirmButtonColor: "#1D84C6",
          confirmButtonText: "Continuar",
          allowOutsideClick: true,
          allowEscapeKey: true,
          allowEnterKey: true,
        });
      }
    } else if (!loading) {
      setShown(false);
      startPolling(POLL_INTERVAL);
      wsrStartPolling(POLL_INTERVAL);
    }
  }, [
    loadingConcreteShipment,
    loading,
    setShown,
    shown,
    startPolling,
    stopPolling,
    wsrStartPolling,
    wsrStopPolling,
  ]);

  useEffect(() => {
    if (waitingShipmentRequest) {
      wsrStopPolling();
      setIsSRMOpen(true);
    } else if (!wsrLoading) {
      wsrStartPolling(POLL_INTERVAL);
    }
  }, [
    waitingShipmentRequest,
    wsrLoading,
    wsrStartPolling,
    wsrStopPolling,
  ]);

  const { devices } = devicesData || {};

  const [device] = devices || [];
  const {
    id,
    quantity,
    concreteFormula,
    controllerFinishedAt,
  } = loadingConcreteShipment || {};

  const {
    code,
    fck,
    concreteFamily,
  } = concreteFormula || {};

  const {
    description,
  } = (concreteFamily && concreteFamily.slump) || {};

  const onFinish = () => {
    window.location.reload();
  };

  useEffect(() => {
  }, [loadingConcreteShipment, loadingConcreteShipmentData]);

  return (
    <React.Fragment>
      <ManualOperation
        concreteShipmentId={id}
        controllerHasFinished={!!controllerFinishedAt}
        refetchConcreteShipmentQuery={refetch}
        manualOperations={manualOperations}
        inventoryItemsData={inventoryItemsData}
      />

      {!loadingConcreteShipment && waitingShipmentRequest && isSRMOpen && (
        <ShipmentRequestModal
          isOpen={isSRMOpen}
          setIsOpen={setIsSRMOpen}
          inventoryItems={inventoryItems}
          inventoryLocations={inventoryLocations}
          shipmentRequest={waitingShipmentRequest}
        />
      )}

      {
        concreteFormula && (
          <div className="formula-info">
            <div className="code-container">
              <span>{`Código: ${loadingConcreteShipment.code}`}</span>
              <div className="copy-button">
                <CopyButton text={loadingConcreteShipment.code} />
              </div>
            </div>
            <span>{`Traço: ${code}`}</span>
            <span>{`FCK: ${fck} | Slump: ${description}`}</span>
            <span>{`Volume: ${quantity}`}</span>
          </div>
        )
      }

      {
        (getCurrentUser && !restrictedUsers.has(getCurrentUser.role)) && (
          <ControlMenu
            show={!loadingConcreteShipment}
            onRefetch={refetch}
            handleControlMenuOpen={handleControlMenuOpen}
            inventoryItemsData={inventoryItemsData}
            monitoringExpanded={expanded}
            expansionHeight={expansionHeight}
            onClick={() => setExpanded(false)}
          />
        )}

      {(sensors && Boolean(sensors.length)) && (
        <Sensors
          sensorsList={sensors}
          controlMenuOpen={isControlMenuOpen}
          monitoringExpanded={expanded}
          expansionHeight={expansionHeight}
        />
      )}

      <div
        className="map-content"
        style={{
          paddingTop: expanded ? expansionHeight : 0,
        }}
      >
        <InstrumentsPanelWrapper>
          <img
            className="plant-background-image"
            src={device && `${window.location.origin}${device.plantImagePath}`}
            alt="Painel de instrumentos"
            style={{
              position: "relative",
              top: 0,
              left: 0,
              width: "100%",
              height: "auto",
            }}
          />
          <InstrumentsPanelContent
            refetchConcreteShipmentQuery={refetch}
            onFinish={onFinish}
            loadingShipment={loadingConcreteShipment}
            plantLocations={device ? device.plantLocations : []}
            automationStatus={automationStatus}
          />
        </InstrumentsPanelWrapper>
      </div>
      {(cameraList.length > 0 && sidebarCollapsed) && (
        <CameraWrapper
          expanded={expanded}
          setExpanded={setExpanded}
          setExpansionHeight={setExpansionHeight}
          expansionHeight={expansionHeight}
          height={CAMERA_HEIGHT}
          cams={cameraList}
          isMobile={isMobile}
        />
      )}
      <Notifications />
    </React.Fragment>
  );
};

export default InstrumentsPanel;
